import {
  ComponentRef,
  Directive,
  Input,
  OnDestroy,
  OnInit,
  ViewContainerRef,
} from '@angular/core';
import {
  IComponentProperties,
  IDynamicItem,
  IDynamicItemStyles,
  IDynamicOutputs,
} from '@epicuro-next/models/platform/dynamic-elements/dynamic-elements';
import { DynamicScope } from '../interfaces/dynamic-scope';

import {
  ComponentsResolverService,
  IComponentOptions,
} from '../services/components-resolver.service';

/** Directive to render dynamic components */
@Directive({
  selector: '[epcDynamicComponent]',
  standalone: false,
})
export class DynamicComponentDirective implements OnInit, OnDestroy {
  private componentRef: ComponentRef<unknown>;

  /** Component properties */
  @Input('epcDynamicComponent')
  public properties: IComponentProperties;
  /** Component data */

  @Input()
  public id?: string;

  @Input()
  public set isRoot(value: boolean) {
    this.isRootInternal = value;
  }
  public get isRoot(): boolean {
    // Backwards compatibility for when no root is set.
    return this.isRootInternal || (this.scope as unknown) === this;
  }
  private isRootInternal = false;

  @Input()
  public set scope(value: DynamicScope) {
    this.__scope__ = value;
  }
  public get scope(): DynamicScope {
    // Backwards compatibility for when no scope is set.
    return this.__scope__ ?? this;
  }
  // tslint:disable-next-line:variable-name
  private __scope__: DynamicScope;

  @Input()
  public inputs: Record<string, unknown>;

  @Input()
  public styles: Partial<IDynamicItemStyles>;

  @Input()
  public children?: IDynamicItem[];

  @Input()
  public data?: Record<string, unknown>;

  @Input()
  public eventHandlers?: IDynamicItem['eventHandlers'];

  @Input()
  public outputs?: IDynamicOutputs;

  @Input()
  public attributes?: { [key: string]: string };

  @Input()
  public events: IDynamicItem['events'];

  /** Dependecies */
  constructor(
    private readonly viewContainerRef: ViewContainerRef,
    private readonly componentsResolverService: ComponentsResolverService,
  ) {}

  /** Angular's OnInit hook */
  public ngOnInit() {
    this.resolveComponent();
  }

  public ngOnDestroy() {
    this.componentRef?.destroy();
    this.viewContainerRef.clear();
  }

  /** Method to create dynamic component */
  private resolveComponent() {
    const options: IComponentOptions = {
      id: this.id,
      isRoot: this.isRoot,
      scope: this.scope,
      styles: this.styles,
      inputs: this.inputs,
      children: this.children,
      data: this.data,
      parent: this.properties.parent,
      eventHandlers: this.eventHandlers,
      outputs: this.outputs,
      attributes: this.attributes,
      events: this.events,
    };

    this.componentRef = this.componentsResolverService.resolveComponent(
      this.properties.contentTypeId,
      this.viewContainerRef,
      options,
    );
  }
}
